import { ActionsPopover, Button, Tooltip, useAppContext } from "@dit/core-frontend"
import React, { memo, useCallback, useState } from "react"
import { buttonProps } from "./buttonProps"
import { Box, Stack, Text } from "@mos-cat/ds"
import { Add, ArrowDown } from "@mos-cat/ds-icons"
import {
  ImageIcon,
  DelimiterIcon,
  PageBreakIcon,
  FileIcon,
  BoardIcon,
  TableIcon,
  ColumnIcon,
  NoteIcon,
  PlayIcon,
} from "../../theme/editorIcons"
import InsertLayoutDialog from "../../plugins/LayoutPlugin/InsertLayoutDialog"
import { INSERT_COLLAPSIBLE_COMMAND } from "../../plugins/CollapsiblePlugin"
import { $getPreviousSelection, $getRoot, $getSelection } from "lexical"
import { $createStickyNode } from "../../nodes/StickyNode"
import { INSERT_PAGE_BREAK } from "../../plugins/PageBreakPlugin"
import { INSERT_EXCALIDRAW_COMMAND } from "../../plugins/ExalidrawPlugin"
import { AutoEmbedDialog, EmbedConfigs } from "../../plugins/AutoEmbdedPlugin"
import { INSERT_EMBED_COMMAND } from "@lexical/react/LexicalAutoEmbedPlugin"
import { ImportPageModal } from "../ImportPageModal"
import { InsertTableDialog } from "../../plugins/TablePlugin"
import { InsertImageModal } from "../InsertImageDialog"
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode"
import { $findMatchingParent } from "@lexical/utils"
import { $isTableRowNode } from "../../nodes/TableNode"
import { MentionButtons } from "./MentionButtons"

export const OtherActionsDropDown = memo(
  ({ activeEditor, onFileUploadStart, disabled, portal = document.body }) => {
    const [showModal, setShowModal] = useState(null)
    const [isTableCreate, setIsTableCreate] = useState(true)

    const checkIsCreateTable = useCallback(() => {
      activeEditor.update(() => {
        const selection = $getSelection() || $getPreviousSelection()
        if (selection !== null) {
          const { anchor, focus } = selection
          const focusNode = focus.getNode()
          const anchorNode = anchor.getNode()
          if (
            $findMatchingParent(focusNode, $isTableRowNode) ||
            $findMatchingParent(anchorNode, $isTableRowNode)
          ) {
            setIsTableCreate(false)
            return
          }
        }
        setIsTableCreate(true)
        return
      })
    }, [activeEditor])

    const { isMobileView } = useAppContext()

    const handleSetModal = useCallback((value) => {
      setShowModal(value)
    }, [])
    return (
      <>
        <ActionsPopover
          disabled={disabled}
          portalContainer={portal ? portal : null}
          content={
            <Tooltip content="Дополнительные инструменты">
              <Button
                {...buttonProps}
                kind="borderless"
                vertSize="32"
                display="flex"
                justifyContent="start"
                alignItems="center"
                gridGap="5px"
              >
                <Box display="flex" onClick={checkIsCreateTable}>
                  {" "}
                  <Add />
                  <Text fontSize="fs-14">
                    <ArrowDown />
                  </Text>
                </Box>
              </Button>
            </Tooltip>
          }
        >
          {(buttonProps, hidePopover) => (
            <>
              <Button
                {...buttonProps}
                onClick={() => handleSetModal("create-image")}
                kind="borderless"
              >
                <ImageIcon />
                <Text>Изображение</Text>
              </Button>

              <Button
                kind="borderless"
                {...buttonProps}
                onClick={() => {
                  onFileUploadStart()
                  hidePopover()
                }}
              >
                <FileIcon />
                <Text>{"Файл"}</Text>
              </Button>
              <Button {...buttonProps} onClick={() => handleSetModal("columns")} kind="borderless">
                <ColumnIcon />
                <Text>Столбцы</Text>
              </Button>
              <Button
                {...buttonProps}
                onClick={() => {
                  activeEditor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined)
                }}
              >
                <PlayIcon />
                <Text>Спойлер</Text>
              </Button>
              {activeEditor._config.namespace === "Editor" && (
                <Button
                  {...buttonProps}
                  onClick={() => {
                    activeEditor.update(() => {
                      const root = $getRoot()
                      const stickyNode = $createStickyNode(0, 0)
                      root.append(stickyNode)
                    })
                  }}
                >
                  <NoteIcon />
                  <Text>Заметка</Text>
                </Button>
              )}

              <Button
                {...buttonProps}
                onClick={() => handleSetModal("import-page")}
                kind="borderless"
              >
                <FileIcon />
                <Text>Импорт страницы</Text>
              </Button>
              <Button
                {...buttonProps}
                onClick={() => {
                  activeEditor.dispatchCommand(INSERT_PAGE_BREAK, undefined)
                }}
              >
                {" "}
                <PageBreakIcon />
                <Text> Разрыв страницы</Text>
              </Button>
              <Button
                {...buttonProps}
                onClick={() => {
                  activeEditor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
                }}
              >
                <DelimiterIcon />
                <Text>Разделитель</Text>
              </Button>
              <Button
                {...buttonProps}
                kind="borderless"
                onClick={() => {
                  activeEditor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined)
                }}
              >
                <BoardIcon />
                <Text>Доска</Text>
              </Button>
              {isTableCreate && (
                <Button
                  {...buttonProps}
                  onClick={() => handleSetModal("create-table")}
                  kind="borderless"
                >
                  <TableIcon />
                  <Text>Таблица</Text>
                </Button>
              )}

              {EmbedConfigs.map((embedConfig) => (
                <AutoEmbedDialog
                  key={embedConfig.type}
                  embedConfig={embedConfig}
                  modalDisclosureProps={{
                    as: Stack,
                    position: "relative",
                    styles: { cursor: "pointer" },
                  }}
                >
                  <Button
                    {...buttonProps}
                    kind="borderless"
                    onClick={() => {
                      activeEditor.dispatchCommand(INSERT_EMBED_COMMAND, embedConfig.type)
                      handleSetModal(embedConfig.type)
                    }}
                  >
                    {embedConfig.icon}
                    <Text>{embedConfig.contentName}</Text>
                  </Button>
                </AutoEmbedDialog>
              ))}
              <MentionButtons activeEditor={activeEditor} buttonProps={buttonProps} />
            </>
          )}
        </ActionsPopover>

        <InsertLayoutDialog
          activeEditor={activeEditor}
          modalDisclosureProps={{
            as: Stack,
            space: "0",
            position: "relative",
            styles: { cursor: "pointer" },
          }}
          modalStateProps={{
            visible: showModal === "columns",
            hide: () => handleSetModal(null),
          }}
        />
        <ImportPageModal
          activeEditor={activeEditor}
          modalDisclosureProps={{
            as: Stack,
            space: "0",
            position: "relative",
            styles: { cursor: "pointer" },
          }}
          modalStateProps={{
            visible: showModal === "import-page",
            hide: () => handleSetModal(null),
          }}
        ></ImportPageModal>
        <InsertTableDialog
          activeEditor={activeEditor}
          modalDisclosureProps={{
            as: Stack,
            space: "0",
            position: "relative",
            styles: { cursor: "pointer" },
          }}
          modalStateProps={{
            visible: showModal === "create-table",
            hide: () => handleSetModal(null),
          }}
        ></InsertTableDialog>
        <InsertImageModal
          activeEditor={activeEditor}
          modalDisclosureProps={{
            as: Stack,
            space: "0",
            position: "relative",
            styles: { cursor: "pointer" },
          }}
          modalStateProps={{
            visible: showModal === "create-image",
            hide: () => handleSetModal(null),
          }}
        ></InsertImageModal>
        {EmbedConfigs.map((embedConfig) => (
          <AutoEmbedDialog
            key={embedConfig.type}
            embedConfig={embedConfig}
            modalDisclosureProps={{
              as: Stack,
              space: "0",
              position: "relative",
              styles: { cursor: "pointer" },
            }}
            modalStateProps={{
              visible: showModal === embedConfig.type,
              hide: () => handleSetModal(null),
            }}
          />
        ))}
      </>
    )
  },
)
OtherActionsDropDown.displayName = "OtherActionsDropDown"
